import { useEffect, useState } from "react";
import { Badge, Btn, Card, I } from "../ui";
import {
  admin,
  organization,
  useActiveOrganization,
  useListOrganizations,
  useSession,
} from "../auth-client";
import { apiDelete, apiGet, apiPatch, apiPost } from "../api";
import { isPlatformAdminRole } from "../../api/platform-roles";
import { getOrgFeatures, setOrgFeatures, type OrgFeatures } from "../api/org-features";

interface MemberRow {
  id: string;
  role: string;
  userId: string;
  email: string;
  name: string;
  createdAt: string;
}

interface UserOption {
  id: string;
  email: string;
  name: string;
}

interface InvitationRow {
  id: string;
  email: string;
  role: string;
  status: string;
  expiresAt: Date | string;
  createdAt: Date | string;
  organizationId: string;
  inviterId: string;
}

type OrgRole = "owner" | "admin" | "member";

/** Accept-from-email atterra su / con ?invitation= (nessuna route dedicata; AuthGate resta invariato). */
export function inviteAcceptUrl(origin: string, invitationId: string): string {
  const base = origin.trim().replace(/\/+$/, "");
  return `${base}/?invitation=${encodeURIComponent(invitationId.trim())}`;
}

function authErrorMessage(
  error: { message?: string | null; code?: string | null } | null | undefined,
  fallback: string,
): string {
  const code = error?.code ?? "";
  if (code === "YOU_ARE_NOT_ALLOWED_TO_INVITE_USERS_TO_THIS_ORGANIZATION") {
    return "Non hai il permesso di invitare utenti in questa organizzazione";
  }
  if (code === "USER_IS_ALREADY_INVITED_TO_THIS_ORGANIZATION") {
    return "Questo indirizzo ha già un invito in sospeso";
  }
  if (code === "USER_IS_ALREADY_A_MEMBER_OF_THIS_ORGANIZATION") {
    return "Questo utente è già membro dell'organizzazione";
  }
  if (code === "YOU_ARE_NOT_ALLOWED_TO_INVITE_USER_WITH_THIS_ROLE") {
    return "Non puoi invitare con il ruolo owner";
  }
  if (code === "MEMBER_NOT_FOUND") {
    return "Devi essere membro (owner o admin) dell'organizzazione per invitare";
  }
  return error?.message?.trim() || fallback;
}

function canCreateInvitation(orgRole: string): boolean {
  return orgRole
    .split(",")
    .map((role) => role.trim())
    .filter(Boolean)
    .some((role) =>
      organization.checkRolePermission({
        role: role as OrgRole,
        permissions: { invitation: ["create"] },
      }),
    );
}

function isPendingInvitation(inv: InvitationRow, now = Date.now()): boolean {
  if (inv.status !== "pending") return false;
  const exp = new Date(inv.expiresAt).getTime();
  return !Number.isFinite(exp) || exp > now;
}

function formatInviteDate(value: Date | string): string {
  const date = value instanceof Date ? value : new Date(value);
  if (!Number.isFinite(date.getTime())) return "—";
  return date.toLocaleString("it-IT");
}

export function Organizations({ pushToast }: { pushToast: (k: "ok" | "err", m: string) => void }) {
  const { data: session } = useSession();
  const { data: orgs, refetch: refetchOrgs } = useListOrganizations();
  const { data: active, refetch: refetchActive } = useActiveOrganization();
  const [members, setMembers] = useState<MemberRow[]>([]);
  const [users, setUsers] = useState<UserOption[]>([]);
  const [showCreate, setShowCreate] = useState(false);
  const [newOrgName, setNewOrgName] = useState("");
  const [newOrgSlug, setNewOrgSlug] = useState("");
  const [showAddMember, setShowAddMember] = useState(false);
  const [addUserId, setAddUserId] = useState("");
  const [addRole, setAddRole] = useState<OrgRole>("member");
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [invitations, setInvitations] = useState<InvitationRow[]>([]);
  const [loadingInvites, setLoadingInvites] = useState(false);
  const [inviteListError, setInviteListError] = useState<string | null>(null);
  const [showInvite, setShowInvite] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<OrgRole>("member");
  const [inviting, setInviting] = useState(false);
  const [lastInvite, setLastInvite] = useState<{ email: string; url: string } | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [features, setFeatures] = useState<OrgFeatures | null>(null);
  const [featuresError, setFeaturesError] = useState<string | null>(null);
  const [savingFeature, setSavingFeature] = useState(false);

  const loadMembers = async (orgId: string) => {
    try {
      setLoadingMembers(true);
      const rows = await apiGet<MemberRow[]>(`/api/orgs/${orgId}/members`);
      setMembers(rows);
    } catch (e) {
      pushToast("err", e instanceof Error ? e.message : "Errore caricamento membri");
    } finally {
      setLoadingMembers(false);
    }
  };

  const loadUsers = async () => {
    try {
      const res = await admin.listUsers({ query: { limit: 200 } });
      if (res.error) throw new Error(res.error.message ?? "Errore");
      const list = (res.data?.users ?? []) as UserOption[];
      setUsers(list);
    } catch {
      /* silent - admin-only endpoint, ignora se non admin */
    }
  };

  const loadInvitations = async (orgId: string) => {
    try {
      setLoadingInvites(true);
      setInviteListError(null);
      const res = await organization.listInvitations({ query: { organizationId: orgId } });
      if (res.error) throw new Error(authErrorMessage(res.error, "Errore caricamento inviti"));
      setInvitations((res.data ?? []) as InvitationRow[]);
    } catch (e) {
      setInvitations([]);
      setInviteListError(e instanceof Error ? e.message : "Errore caricamento inviti");
    } finally {
      setLoadingInvites(false);
    }
  };

  useEffect(() => {
    void loadUsers();
  }, []);

  useEffect(() => {
    if (active?.id) void loadMembers(active.id);
    else setMembers([]);
  }, [active?.id]);

  const isPlatformAdmin = isPlatformAdminRole(session?.user?.role);
  const myMember = members.find((m) => m.userId === session?.user?.id);
  const orgRole = myMember?.role ?? "";
  const canInviteViaPlugin = canCreateInvitation(orgRole);
  const canInvite = isPlatformAdmin || canInviteViaPlugin;
  const canInviteOwner = orgRole.split(",").map((r) => r.trim()).includes("owner");

  useEffect(() => {
    setFeatures(null);
    setFeaturesError(null);
    if (!active?.id || !isPlatformAdmin) return;
    const controller = new AbortController();
    getOrgFeatures(controller.signal)
      .then(setFeatures)
      .catch((e: unknown) => { if (!controller.signal.aborted) setFeaturesError(e instanceof Error ? e.message : "Errore caricamento funzioni"); });
    return () => controller.abort();
  }, [active?.id, isPlatformAdmin]);

  const onToggleFeature = async (feature: keyof OrgFeatures, enabled: boolean) => {
    try {
      setSavingFeature(true);
      setFeatures(await setOrgFeatures({ [feature]: enabled }));
      pushToast("ok", enabled ? "Funzione attivata" : "Funzione disattivata");
    } catch (e) {
      pushToast("err", e instanceof Error ? e.message : "Errore salvataggio funzione");
    } finally {
      setSavingFeature(false);
    }
  };

  useEffect(() => {
    if (active?.id && canInvite) void loadInvitations(active.id);
    else {
      setInvitations([]);
      setInviteListError(null);
      setLastInvite(null);
    }
  }, [active?.id, canInvite]);

  const slugify = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 40);

  const onCreateOrg = async () => {
    if (!newOrgName.trim()) {
      pushToast("err", "Nome richiesto");
      return;
    }
    try {
      const slug = newOrgSlug.trim() || slugify(newOrgName);
      const res = await organization.create({ name: newOrgName.trim(), slug });
      if (res.error) throw new Error(res.error.message ?? "Errore");
      pushToast("ok", `Org "${newOrgName}" creata`);
      setNewOrgName("");
      setNewOrgSlug("");
      setShowCreate(false);
      await refetchOrgs();
      if (res.data) {
        await organization.setActive({ organizationId: res.data.id });
        await refetchActive();
      }
    } catch (e) {
      pushToast("err", e instanceof Error ? e.message : "Errore create org");
    }
  };

  const onSetActive = async (id: string) => {
    try {
      await organization.setActive({ organizationId: id });
      await refetchActive();
    } catch (e) {
      pushToast("err", e instanceof Error ? e.message : "Errore");
    }
  };

  const onDeleteOrg = async () => {
    if (!active) return;
    if (!confirm(`Eliminare l'organizzazione "${active.name}"? I membri verranno rimossi.`)) return;
    try {
      const res = await organization.delete({ organizationId: active.id });
      if (res.error) throw new Error(res.error.message ?? "Errore");
      pushToast("ok", `Org "${active.name}" eliminata`);
      await refetchOrgs();
      await refetchActive();
    } catch (e) {
      pushToast("err", e instanceof Error ? e.message : "Errore delete");
    }
  };

  const onAddMember = async () => {
    if (!active || !addUserId) return;
    try {
      await apiPost(`/api/orgs/${active.id}/members`, { userId: addUserId, role: addRole });
      pushToast("ok", "Membro aggiunto");
      setShowAddMember(false);
      setAddUserId("");
      setAddRole("member");
      await loadMembers(active.id);
    } catch (e) {
      pushToast("err", e instanceof Error ? e.message : "Errore");
    }
  };

  const onUpdateRole = async (m: MemberRow, role: OrgRole) => {
    if (!active) return;
    try {
      await apiPatch(`/api/orgs/${active.id}/members/${m.id}`, { role });
      setMembers((prev) => prev.map((x) => (x.id === m.id ? { ...x, role } : x)));
    } catch (e) {
      pushToast("err", e instanceof Error ? e.message : "Errore");
    }
  };

  const onRemoveMember = async (m: MemberRow) => {
    if (!active) return;
    if (!confirm(`Rimuovere ${m.email} da ${active.name}?`)) return;
    try {
      await apiDelete(`/api/orgs/${active.id}/members/${m.id}`);
      setMembers((prev) => prev.filter((x) => x.id !== m.id));
      pushToast("ok", "Membro rimosso");
    } catch (e) {
      pushToast("err", e instanceof Error ? e.message : "Errore");
    }
  };

  const copyInviteLink = async (invitationId: string) => {
    const url = inviteAcceptUrl(window.location.origin, invitationId);
    try {
      await navigator.clipboard.writeText(url);
      setCopiedId(invitationId);
      pushToast("ok", "Link di invito copiato");
      window.setTimeout(() => {
        setCopiedId((current) => (current === invitationId ? null : current));
      }, 2000);
    } catch {
      window.prompt("Copia il link di invito", url);
    }
  };

  const onInvite = async () => {
    if (!active || !canInvite) return;
    const email = inviteEmail.trim().toLowerCase();
    if (!email || !email.includes("@")) {
      pushToast("err", "Email non valida");
      return;
    }
    const role = inviteRole === "owner" && !canInviteOwner ? "member" : inviteRole;
    try {
      setInviting(true);
      const res = await organization.inviteMember({
        email,
        role,
        organizationId: active.id,
      });
      if (res.error) throw new Error(authErrorMessage(res.error, "Errore invito"));
      const invitationId = res.data?.id ?? "";
      const url = invitationId ? inviteAcceptUrl(window.location.origin, invitationId) : "";
      setLastInvite(url ? { email, url } : null);
      setInviteEmail("");
      setInviteRole("member");
      setShowInvite(false);
      pushToast("ok", `Invito creato per ${email}. Copia il link: l'email automatica dipende dal mailer.`);
      await loadInvitations(active.id);
    } catch (e) {
      pushToast("err", e instanceof Error ? e.message : "Errore invito");
    } finally {
      setInviting(false);
    }
  };

  const onCancelInvitation = async (inv: InvitationRow) => {
    if (!active) return;
    if (!confirm(`Annullare l'invito a ${inv.email}?`)) return;
    try {
      const res = await organization.cancelInvitation({ invitationId: inv.id });
      if (res.error) throw new Error(authErrorMessage(res.error, "Errore annulla invito"));
      setInvitations((prev) => prev.filter((x) => x.id !== inv.id));
      if (lastInvite?.email === inv.email) setLastInvite(null);
      pushToast("ok", "Invito annullato");
    } catch (e) {
      pushToast("err", e instanceof Error ? e.message : "Errore annulla invito");
    }
  };

  const orgList = orgs ?? [];
  const memberUserIds = new Set(members.map((m) => m.userId));
  const availableUsers = users.filter((u) => !memberUserIds.has(u.id));
  const pendingInvites = invitations.filter((inv) => isPendingInvitation(inv));

  return (
    <div className="col gap-16">
      <Card
        title="Organizzazioni (aziende)"
        sub="container multi-tenant · ogni utente può far parte di più aziende"
        actions={
          <div className="row gap-6">
            <Btn size="sm" variant="ghost" icon={I.refresh} onClick={() => { void refetchOrgs(); void refetchActive(); }}>Refresh</Btn>
            {isPlatformAdmin && (
              <Btn size="sm" variant="primary" icon={I.plus} onClick={() => setShowCreate(!showCreate)}>
                {showCreate ? "Annulla" : "Crea org"}
              </Btn>
            )}
          </div>
        }
      >
        {isPlatformAdmin && showCreate && (
          <div className="preview-box" style={{ marginBottom: 14, padding: 14, display: "flex", flexDirection: "column", gap: 10 }}>
            <div className="row gap-8" style={{ flexWrap: "wrap" }}>
              <div className="field" style={{ flex: 1, minWidth: 200 }}>
                <label className="label">Nome azienda</label>
                <input className="input" value={newOrgName} onChange={(e) => setNewOrgName(e.target.value)} placeholder="Acme SRL" />
              </div>
              <div className="field" style={{ flex: 1, minWidth: 180 }}>
                <label className="label">Slug (URL)</label>
                <input className="input mono" value={newOrgSlug} onChange={(e) => setNewOrgSlug(e.target.value)} placeholder={slugify(newOrgName) || "acme"} />
              </div>
            </div>
            <div className="row gap-8" style={{ justifyContent: "flex-end" }}>
              <Btn size="sm" variant="primary" onClick={() => void onCreateOrg()}>Crea</Btn>
            </div>
          </div>
        )}

        {orgList.length === 0 ? (
          <div style={{ padding: 24, textAlign: "center", color: "var(--text-2)", fontSize: 13 }}>
            Nessuna organizzazione. Creane una per iniziare.
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table className="tbl" style={{ width: "100%", fontSize: 12 }}>
              <thead>
                <tr>
                  <th style={{ textAlign: "left", padding: "8px 12px" }}>Attiva</th>
                  <th style={{ textAlign: "left", padding: "8px 12px" }}>Nome</th>
                  <th style={{ textAlign: "left", padding: "8px 12px" }}>Slug</th>
                  <th style={{ textAlign: "left", padding: "8px 12px" }}>Creata</th>
                </tr>
              </thead>
              <tbody>
                {orgList.map((o) => (
                  <tr key={o.id} style={{ borderTop: "1px solid var(--line)", cursor: "pointer" }} onClick={() => void onSetActive(o.id)}>
                    <td style={{ padding: "8px 12px" }}>
                      {active?.id === o.id ? <Badge kind="accent">attiva</Badge> : <Badge kind="muted">—</Badge>}
                    </td>
                    <td style={{ padding: "8px 12px", fontWeight: 500 }}>{o.name}</td>
                    <td className="mono" style={{ padding: "8px 12px", color: "var(--text-3)" }}>{o.slug}</td>
                    <td style={{ padding: "8px 12px", color: "var(--text-2)" }}>
                      {new Date(o.createdAt).toLocaleDateString("it-IT")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {active && (
        <Card
          title={`Membri di ${active.name}`}
          sub="gestisci chi ha accesso e con quale ruolo"
          actions={
            <div className="row gap-6">
              {isPlatformAdmin && (
                <>
                  <Btn size="sm" variant="danger" onClick={() => void onDeleteOrg()}>Elimina org</Btn>
                  <Btn size="sm" variant="primary" icon={I.plus} onClick={() => setShowAddMember(!showAddMember)}>
                    {showAddMember ? "Annulla" : "Aggiungi membro"}
                  </Btn>
                </>
              )}
            </div>
          }
        >
          {isPlatformAdmin && showAddMember && (
            <div className="preview-box" style={{ marginBottom: 14, padding: 14, display: "flex", flexDirection: "column", gap: 10 }}>
              <div className="row gap-8" style={{ flexWrap: "wrap" }}>
                <div className="field" style={{ flex: 1, minWidth: 240 }}>
                  <label className="label">Utente</label>
                  <select className="select" value={addUserId} onChange={(e) => setAddUserId(e.target.value)}>
                    <option value="">Seleziona…</option>
                    {availableUsers.map((u) => (
                      <option key={u.id} value={u.id}>{u.email} — {u.name}</option>
                    ))}
                  </select>
                </div>
                <div className="field" style={{ width: 140 }}>
                  <label className="label">Ruolo</label>
                  <select className="select" value={addRole} onChange={(e) => setAddRole(e.target.value as typeof addRole)}>
                    <option value="member">member</option>
                    <option value="admin">admin</option>
                    <option value="owner">owner</option>
                  </select>
                </div>
              </div>
              <div className="row gap-8" style={{ justifyContent: "flex-end" }}>
                <Btn size="sm" variant="primary" onClick={() => void onAddMember()}>Aggiungi</Btn>
              </div>
            </div>
          )}

          {loadingMembers ? (
            <div style={{ padding: 20, color: "var(--text-2)", fontSize: 13 }}>Caricamento…</div>
          ) : members.length === 0 ? (
            <div style={{ padding: 24, textAlign: "center", color: "var(--text-2)", fontSize: 13 }}>
              Nessun membro.
            </div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table className="tbl" style={{ width: "100%", fontSize: 12 }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: "left", padding: "8px 12px" }}>Email</th>
                    <th style={{ textAlign: "left", padding: "8px 12px" }}>Nome</th>
                    <th style={{ textAlign: "left", padding: "8px 12px" }}>Ruolo</th>
                    <th style={{ textAlign: "left", padding: "8px 12px" }}>Aggiunto</th>
                    <th style={{ textAlign: "right", padding: "8px 12px" }}>Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {members.map((m) => (
                    <tr key={m.id} style={{ borderTop: "1px solid var(--line)" }}>
                      <td style={{ padding: "8px 12px" }}>{m.email}</td>
                      <td style={{ padding: "8px 12px" }}>{m.name}</td>
                      <td style={{ padding: "8px 12px" }}>
                        <select
                          className="select"
                          style={{ height: 26, fontSize: 12, padding: "0 8px" }}
                          value={m.role}
                          onChange={(e) => void onUpdateRole(m, e.target.value as OrgRole)}
                        >
                          <option value="member">member</option>
                          <option value="admin">admin</option>
                          <option value="owner">owner</option>
                        </select>
                      </td>
                      <td style={{ padding: "8px 12px", color: "var(--text-2)" }}>
                        {new Date(m.createdAt).toLocaleDateString("it-IT")}
                      </td>
                      <td style={{ padding: "8px 12px", textAlign: "right" }}>
                        <Btn size="sm" variant="danger" onClick={() => void onRemoveMember(m)}>Rimuovi</Btn>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {active && isPlatformAdmin && (
        <Card title={`Funzioni di ${active.name}`} sub="visibile solo al super admin · vale per tutti gli utenti dell'azienda">
          {features === null ? (
            <div style={{ padding: 12, color: "var(--text-2)", fontSize: 13 }}>{featuresError ?? "Caricamento…"}</div>
          ) : (
            <label className="row gap-8" style={{ padding: "8px 12px", fontSize: 13, cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={features.prospectsExcel}
                disabled={savingFeature}
                onChange={(e) => void onToggleFeature("prospectsExcel", e.target.checked)}
              />
              <span>
                <strong>Esporta Excel dei Prospect</strong>
                <span style={{ color: "var(--text-2)" }}> · pulsante «Esporta Excel» nella pagina Prospect</span>
              </span>
            </label>
          )}
        </Card>
      )}

      {active && canInvite && (
        <Card
          title="Inviti in sospeso"
          sub="invita clienti per email · se il mailer non è attivo, copia il link e invialo a mano"
          actions={
            <div className="row gap-6">
              <Btn size="sm" variant="ghost" icon={I.refresh} onClick={() => { void loadInvitations(active.id); }}>Refresh</Btn>
              <Btn size="sm" variant="primary" icon={I.plus} onClick={() => setShowInvite(!showInvite)}>
                {showInvite ? "Annulla" : "Invita per email"}
              </Btn>
            </div>
          }
        >
          {showInvite && (
            <form
              className="preview-box"
              style={{ marginBottom: 14, padding: 14, display: "flex", flexDirection: "column", gap: 10 }}
              onSubmit={(e) => { e.preventDefault(); void onInvite(); }}
            >
              {!canInviteViaPlugin && (
                <div style={{ color: "var(--warn)", fontSize: 12 }}>
                  Per invitare devi essere owner o admin di questa organizzazione.
                  Aggiungiti come membro con quel ruolo, poi riprova.
                </div>
              )}
              <div className="row gap-8" style={{ flexWrap: "wrap" }}>
                <div className="field" style={{ flex: 1, minWidth: 240 }}>
                  <label className="label">Email cliente</label>
                  <input
                    className="input"
                    type="email"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    placeholder="cliente@azienda.it"
                    autoComplete="email"
                    required
                  />
                </div>
                <div className="field" style={{ width: 140 }}>
                  <label className="label">Ruolo</label>
                  <select className="select" value={inviteRole} onChange={(e) => setInviteRole(e.target.value as OrgRole)}>
                    <option value="member">member</option>
                    <option value="admin">admin</option>
                    {canInviteOwner && <option value="owner">owner</option>}
                  </select>
                </div>
              </div>
              <div style={{ color: "var(--text-2)", fontSize: 12 }}>
                Il cliente deve registrarsi o accedere con la stessa email, poi accettare l&apos;invito.
                Il link apre la console su <span className="mono">/?invitation=</span>.
              </div>
              <div className="row gap-8" style={{ justifyContent: "flex-end" }}>
                <Btn size="sm" variant="primary" disabled={inviting}>
                  {inviting ? "Invio…" : "Invita"}
                </Btn>
              </div>
            </form>
          )}

          {lastInvite && (
            <div className="preview-box" style={{ marginBottom: 14, padding: 14, display: "flex", flexDirection: "column", gap: 8 }}>
              <div style={{ fontSize: 12, color: "var(--text-1)" }}>
                Link per <strong>{lastInvite.email}</strong> — copialo e invialo al cliente.
              </div>
              <div className="row gap-8" style={{ flexWrap: "wrap", alignItems: "center" }}>
                <input className="input mono" readOnly value={lastInvite.url} style={{ flex: 1, minWidth: 220 }} />
                <Btn size="sm" variant="primary" onClick={() => void navigator.clipboard.writeText(lastInvite.url).then(
                  () => pushToast("ok", "Link di invito copiato"),
                  () => window.prompt("Copia il link di invito", lastInvite.url),
                )}>
                  Copia link
                </Btn>
              </div>
            </div>
          )}

          {inviteListError && (
            <div style={{ padding: "8px 0 14px", color: "var(--danger)", fontSize: 12 }}>{inviteListError}</div>
          )}

          {loadingInvites ? (
            <div style={{ padding: 20, color: "var(--text-2)", fontSize: 13 }}>Caricamento…</div>
          ) : inviteListError ? null : pendingInvites.length === 0 ? (
            <div style={{ padding: 24, textAlign: "center", color: "var(--text-2)", fontSize: 13 }}>
              Nessun invito in sospeso.
            </div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table className="tbl" style={{ width: "100%", fontSize: 12 }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: "left", padding: "8px 12px" }}>Email</th>
                    <th style={{ textAlign: "left", padding: "8px 12px" }}>Ruolo</th>
                    <th style={{ textAlign: "left", padding: "8px 12px" }}>Scade</th>
                    <th style={{ textAlign: "right", padding: "8px 12px" }}>Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {pendingInvites.map((inv) => (
                    <tr key={inv.id} style={{ borderTop: "1px solid var(--line)" }}>
                      <td style={{ padding: "8px 12px" }}>{inv.email}</td>
                      <td style={{ padding: "8px 12px" }}><Badge kind="muted">{inv.role}</Badge></td>
                      <td style={{ padding: "8px 12px", color: "var(--text-2)" }}>{formatInviteDate(inv.expiresAt)}</td>
                      <td style={{ padding: "8px 12px", textAlign: "right" }}>
                        <div className="row gap-6" style={{ justifyContent: "flex-end" }}>
                          <Btn size="sm" variant="ghost" onClick={() => void copyInviteLink(inv.id)}>
                            {copiedId === inv.id ? "Copiato" : "Copia link"}
                          </Btn>
                          <Btn size="sm" variant="danger" onClick={() => void onCancelInvitation(inv)}>Annulla</Btn>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
